import { useRef, useState } from 'react';
import { Upload, Button, Card, Select, Alert, Progress, Space, Typography, Steps, Tag, App, Result, Radio, Tooltip, Collapse, theme } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  UploadOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  DownloadOutlined,
  InfoCircleOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { trpc } from '../lib/api';
import type { UploadProps } from 'antd';
import { ResponsiveTable, type ResponsiveColumnType } from '../components/common/responsive';
import { PageContainer, PageHeader, DevFilePathBadge } from '../components/common';

const { Text } = Typography;
const { Option } = Select;
const { Dragger } = Upload;

type ImportType = 'locations' | 'equipments' | 'parts' | 'suppliers';
type DuplicateHandling = 'upsert' | 'skip' | 'error';
type Confidence = 'high' | 'medium' | 'low';

type Primitive = string | number | boolean | null | undefined;

type PreviewRecord = {
  [key: string]: Primitive;
  _row?: number;
  _status?: 'valid' | 'invalid';
};

// サーバーから返されるプレビューレスポンス形式
interface ServerPreviewResponse {
  headers: string[];
  rows: Record<string, unknown>[];
  totalRows: number;
}

interface PreviewData {
  rows: PreviewRecord[];
  columns: string[];
  totalRows: number;
}

// サーバーから返されるインポート結果形式
interface ServerImportResult {
  success: boolean;
  importedCount: number;
  errors: { row: number; field?: string; message: string }[];
  message: string;
}

interface ImportError {
  row: number;
  field?: string;
  message: string;
}

interface ImportResultState {
  imported: number;
  skipped: number;
  errors: ImportError[];
}

// タイプ判定結果
interface TypeDetectionResult {
  detectedType: ImportType | null;
  confidence: Confidence;
  scores: Record<ImportType, number>;
  reason: string;
  matchedHeaders: string[];
  unmatchedHeaders: string[];
  headers: string[];
}


// サーバーレスポンスをクライアント用に変換
const normalizePreviewData = (response: ServerPreviewResponse): PreviewData => {
  const rows: PreviewRecord[] = response.rows.map((row, index) => ({
    ...row as Record<string, Primitive>,
    _row: index + 2, // ヘッダー行を考慮
  }));

  return {
    rows,
    columns: response.headers,
    totalRows: response.totalRows,
  };
};

const normalizeImportResult = (result: ServerImportResult): ImportResultState => ({
  imported: result.importedCount,
  skipped: result.errors.length,
  errors: result.errors,
});

const isArrayBuffer = (value: unknown): value is ArrayBuffer =>
  Object.prototype.toString.call(value) === '[object ArrayBuffer]';

const arrayBufferToBase64 = (arrayBuffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(arrayBuffer);
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary);
};

export function ImportPage() {
  const { message } = App.useApp();
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const [importType, setImportType] = useState<ImportType | null>(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importResult, setImportResult] = useState<ImportResultState | null>(null);
  const [fileContent, setFileContent] = useState<string>('');
  const [duplicateHandling, setDuplicateHandling] = useState<DuplicateHandling>('upsert');
  const [typeDetection, setTypeDetection] = useState<TypeDetectionResult | null>(null);
  const [isDetecting, setIsDetecting] = useState(false);
  const [uploadInputKey, setUploadInputKey] = useState(0);
  const uploadVersionRef = useRef(0);

  // CSVテンプレートダウンロード用のユーティリティ
  const trpcUtils = trpc.useUtils();

  const handleDownloadTemplate = async (type: ImportType) => {
    try {
      const data = await trpcUtils.import.getMasterTemplate.fetch({ type });
      // Base64デコードしてCSVファイルとしてダウンロード
      const binaryString = atob(data.content);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', data.filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      message.success('テンプレートをダウンロードしました');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : '不明なエラーが発生しました';
      message.error(`エラー: ${errorMessage}`);
    }
  };

  // タイプ自動判定
  const detectTypeMutation = trpc.import.detectImportType.useMutation();

  // CSVプレビュー
  const previewMutation = trpc.import.previewMasterData.useMutation();

  // インポート実行
  const executeMutation = trpc.import.executeMasterImport.useMutation();

  // ファイルアップロード設定（自動タイプ判定）
  const clearLoadedFileState = () => {
    setPreviewData(null);
    setImportResult(null);
    setFileContent('');
    setImportType(null);
    setTypeDetection(null);
    setDuplicateHandling('upsert');
    setImportProgress(0);
  };

  const resetUploadInput = () => {
    setUploadInputKey((key) => key + 1);
  };

  const uploadProps: UploadProps = {
    name: 'file',
    multiple: false,
    accept: '.csv',
    beforeUpload: (file) => {
      const uploadVersion = uploadVersionRef.current + 1;
      uploadVersionRef.current = uploadVersion;
      clearLoadedFileState();
      setIsDetecting(true);
      const reader = new FileReader();
      reader.onload = (e) => {
        if (uploadVersion !== uploadVersionRef.current) return;
        const arrayBuffer = e.target?.result;
        if (!isArrayBuffer(arrayBuffer)) {
          message.error('CSVファイルの読み込みに失敗しました');
          setIsDetecting(false);
          resetUploadInput();
          return;
        }
        const base64 = arrayBufferToBase64(arrayBuffer);
        setFileContent(base64);
        // タイプ自動判定を実行
        detectTypeMutation.mutate(
          { fileContent: base64 },
          {
            onSuccess: (data) => {
              if (uploadVersion !== uploadVersionRef.current) return;
              setTypeDetection(data as TypeDetectionResult);
              if (data.detectedType) {
                setImportType(data.detectedType);
              }
              setIsDetecting(false);
              setCurrentStep(1);
            },
            onError: (error) => {
              if (uploadVersion !== uploadVersionRef.current) return;
              message.error(`タイプ判定エラー: ${error.message}`);
              setIsDetecting(false);
            },
          },
        );
        resetUploadInput();
      };
      reader.onerror = () => {
        if (uploadVersion !== uploadVersionRef.current) return;
        message.error('CSVファイルの読み込みに失敗しました');
        setIsDetecting(false);
        resetUploadInput();
      };
      reader.readAsArrayBuffer(file);
      return Upload.LIST_IGNORE;
    },
  };

  const handleConfirmType = () => {
    if (!importType || !fileContent) return;
    const uploadVersion = uploadVersionRef.current;
    previewMutation.mutate(
      {
        type: importType,
        fileContent: fileContent,
      },
      {
        onSuccess: (data: ServerPreviewResponse) => {
          if (uploadVersion !== uploadVersionRef.current) return;
          const normalized = normalizePreviewData(data);
          setPreviewData(normalized);
          setCurrentStep(2);
          message.success(`${normalized.totalRows}件のデータを読み込みました`);
        },
        onError: (error) => {
          if (uploadVersion !== uploadVersionRef.current) return;
          message.error(`プレビューエラー: ${error.message}`);
        },
      },
    );
  };

  const handleImport = () => {
    if (!previewData || !fileContent || !importType) return;

    setImporting(true);
    setImportProgress(0);

    // プログレスバーのシミュレーション
    const timer = setInterval(() => {
      setImportProgress((prev) => {
        if (prev >= 95) {
          clearInterval(timer);
          return 95;
        }
        return prev + 5;
      });
    }, 200);

    const uploadVersion = uploadVersionRef.current;
    executeMutation.mutate({
      type: importType,
      fileContent: fileContent,
      duplicateHandling: duplicateHandling,
    }, {
      onSuccess: (result: ServerImportResult) => {
        if (uploadVersion !== uploadVersionRef.current) return;
        setImportResult(normalizeImportResult(result));
        setCurrentStep(3);
        if (result.success) {
          message.success(result.message);
        } else {
          message.warning(result.message);
        }
      },
      onError: (error) => {
        if (uploadVersion !== uploadVersionRef.current) return;
        message.error(`インポートエラー: ${error.message}`);
        setImporting(false);
      },
      onSettled: () => {
        clearInterval(timer);
        if (uploadVersion === uploadVersionRef.current) {
          setImportProgress(100);
          setImporting(false);
        }
      },
    });
  };

  const getImportTypeLabel = (type: ImportType) => {
    const labels: Record<ImportType, string> = {
      locations: 'エリア',
      equipments: '設備',
      parts: '部品・消耗品',
      suppliers: '業者',
    };
    return labels[type];
  };

  const getConfidenceColor = (confidence: Confidence) => {
    const colors: Record<Confidence, string> = {
      high: 'green',
      medium: 'orange',
      low: 'red',
    };
    return colors[confidence];
  };

  const getConfidenceLabel = (confidence: Confidence) => {
    const labels: Record<Confidence, string> = {
      high: '高',
      medium: '中',
      low: '低',
    };
    return labels[confidence];
  };

  const getPreviewColumns = (): ResponsiveColumnType<PreviewRecord>[] => {
    if (!previewData) return [];

    return previewData.columns.map((col, index) => ({
      title: col,
      dataIndex: col,
      key: col,
      width: 150,
      ellipsis: true,
      mobileRole: index === 0 ? 'title' : index === 1 ? 'tag' : 'description',
      mobilePriority: index + 1,
    }));
  };

  // スクロール幅を動的に計算（カラム数 × 150px）
  const getTableScrollWidth = (): number => {
    if (!previewData) return 800;
    return Math.max(previewData.columns.length * 150, 800);
  };

  const resetImport = () => {
    uploadVersionRef.current += 1;
    setCurrentStep(0);
    setIsDetecting(false);
    clearLoadedFileState();
    resetUploadInput();
  };

  const steps = [
    {
      title: 'ファイル選択',
      icon: <UploadOutlined />,
    },
    {
      title: 'タイプ確認',
      icon: <FileTextOutlined />,
    },
    {
      title: 'プレビュー確認',
      icon: <FileTextOutlined />,
    },
    {
      title: 'インポート完了',
      icon: <CheckCircleOutlined />,
    },
  ];

  return (
    <PageContainer>
      <DevFilePathBadge filePath="client/src/pages/ImportPage.tsx" />
      <PageHeader
        title="データインポート"
        subtitle="CSVファイルを使用して、エリア、設備、部品・消耗品、業者のマスターデータを一括インポートできます"
      />

      <Steps current={currentStep} items={steps} style={{ marginBottom: 24 }} />

      {/* Step 0: ファイル選択 */}
      {currentStep === 0 && (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Card title="テンプレートダウンロード">
            <Space wrap>
              <Button icon={<DownloadOutlined />} onClick={() => handleDownloadTemplate('locations')}>
                エリア
              </Button>
              <Button icon={<DownloadOutlined />} onClick={() => handleDownloadTemplate('equipments')}>
                設備
              </Button>
              <Button icon={<DownloadOutlined />} onClick={() => handleDownloadTemplate('parts')}>
                部品・消耗品
              </Button>
              <Button icon={<DownloadOutlined />} onClick={() => handleDownloadTemplate('suppliers')}>
                業者
              </Button>
            </Space>
          </Card>

          <Card>
            <Dragger key={uploadInputKey} {...uploadProps} style={{ padding: 40 }} disabled={isDetecting}>
              <p className="ant-upload-drag-icon">
                <UploadOutlined style={{ fontSize: 48, color: '#1890ff' }} />
              </p>
              <p className="ant-upload-text">
                {isDetecting ? '判定中...' : 'クリックまたはドラッグしてCSVファイルをアップロード'}
              </p>
              <p className="ant-upload-hint">
                ファイルの内容から自動的にデータタイプを判定します
              </p>
            </Dragger>

            <Alert
              message="インポート前の注意事項"
              description={
                <ul style={{ marginBottom: 0 }}>
                  <li>CSVファイルはShift-JISまたはUTF-8エンコーディングに対応</li>
                  <li>ヘッダー行は必須です</li>
                  <li>日付形式: YYYY-MM-DD</li>
                  <li>最大ファイルサイズ: 10MB</li>
                </ul>
              }
              type="info"
              showIcon
              icon={<InfoCircleOutlined />}
              style={{ marginTop: 16 }}
            />

            <Alert
              message="「○○ID」列の入力について（初心者向け）"
              description={
                <div>
                  <div
                    style={{
                      background: token.colorSuccessBg,
                      padding: '12px',
                      borderRadius: '4px',
                      border: `1px solid ${token.colorSuccessBorder}`,
                    }}
                  >
                    <Text strong style={{ fontSize: 14 }}>✅ かんたんな方法（おすすめ）</Text>
                    <ol style={{ marginBottom: 0, marginTop: 8, paddingLeft: 20 }}>
                      <li>「○○ID」列は<Text strong>空欄のまま</Text>インポート</li>
                      <li>インポート後、各データの編集画面で関連付けを設定</li>
                    </ol>
                    <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                      → 画面上で選択するだけなので、IDを調べる必要がありません
                    </Text>
                  </div>

                  <Collapse
                    ghost
                    style={{ marginTop: 12 }}
                    items={[
                      {
                        key: '1',
                        label: <Text type="secondary">▼ CSVでIDを指定したい場合（上級者向け）</Text>,
                        children: (
                          <div style={{ paddingTop: 8 }}>
                            <p style={{ marginBottom: 8 }}>
                              <Text strong>IDの調べ方:</Text> 「設定 → エクスポート」から対象データをエクスポートすると、CSVの1列目にIDが記載されています。
                            </p>
                            <div
                              style={{
                                background: token.colorFillQuaternary,
                                padding: '12px',
                                borderRadius: '4px',
                                border: `1px solid ${token.colorBorderSecondary}`,
                              }}
                            >
                              <Text strong style={{ display: 'block', marginBottom: 8 }}>各データで使用するID列:</Text>
                              <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                                <li><Text code>エリア</Text>: 親エリアID</li>
                                <li><Text code>設備</Text>: エリアID、親設備ID、メーカー業者ID、販売業者ID、保守業者ID</li>
                                <li><Text code>部品・消耗品</Text>: メーカー業者ID、業者ID</li>
                                <li><Text code>業者</Text>: なし</li>
                              </ul>
                            </div>
                            <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                              ※ IDを指定する場合、参照先データが先に登録されている必要があります
                            </Text>
                          </div>
                        ),
                      },
                    ]}
                  />
                </div>
              }
              type="info"
              showIcon
              icon={<QuestionCircleOutlined />}
              style={{ marginTop: 16 }}
            />
          </Card>
        </Space>
      )}

      {/* Step 1: タイプ確認 */}
      {currentStep === 1 && typeDetection && (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Card title="データタイプ判定結果">
            {typeDetection.detectedType ? (
              <>
                <Alert
                  message={
                    <Space>
                      <Text strong>判定結果: {getImportTypeLabel(typeDetection.detectedType)}データ</Text>
                      <Tag color={getConfidenceColor(typeDetection.confidence)}>
                        信頼度: {getConfidenceLabel(typeDetection.confidence)}
                      </Tag>
                    </Space>
                  }
                  description={typeDetection.reason}
                  type={typeDetection.confidence === 'high' ? 'success' : typeDetection.confidence === 'medium' ? 'warning' : 'error'}
                  showIcon
                  style={{ marginBottom: 16 }}
                />

                <div style={{ marginBottom: 16 }}>
                  <Text strong>インポートタイプを変更: </Text>
                  <Select
                    value={importType}
                    onChange={setImportType}
                    style={{ width: 200, marginLeft: 16 }}
                  >
                    <Option value="locations">エリア</Option>
                    <Option value="equipments">設備</Option>
                    <Option value="parts">部品・消耗品</Option>
                    <Option value="suppliers">業者</Option>
                  </Select>
                </div>

                {typeDetection.matchedHeaders.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <Text type="secondary">マッチしたヘッダー: </Text>
                    <Space wrap size={[4, 4]} style={{ marginTop: 4 }}>
                      {typeDetection.matchedHeaders.slice(0, 10).map((h) => (
                        <Tag key={h} color="blue">{h}</Tag>
                      ))}
                      {typeDetection.matchedHeaders.length > 10 && (
                        <Tag>他 {typeDetection.matchedHeaders.length - 10} 件</Tag>
                      )}
                    </Space>
                  </div>
                )}

                {typeDetection.unmatchedHeaders.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <Text type="secondary">
                      認識されなかったヘッダー:
                      <Tooltip title="これらのヘッダーはインポート時に無視されます">
                        <QuestionCircleOutlined style={{ marginLeft: 4 }} />
                      </Tooltip>
                    </Text>
                    <Space wrap size={[4, 4]} style={{ marginTop: 4 }}>
                      {typeDetection.unmatchedHeaders.slice(0, 5).map((h) => (
                        <Tag key={h} color="default">{h}</Tag>
                      ))}
                      {typeDetection.unmatchedHeaders.length > 5 && (
                        <Tag>他 {typeDetection.unmatchedHeaders.length - 5} 件</Tag>
                      )}
                    </Space>
                  </div>
                )}
              </>
            ) : (
              <>
                <Alert
                  message="タイプを判定できませんでした"
                  description="ヘッダーがどのタイプにも一致しませんでした。手動でタイプを選択してください。"
                  type="error"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
                <div>
                  <Text strong>インポートタイプを選択: </Text>
                  <Select
                    value={importType}
                    onChange={setImportType}
                    style={{ width: 200, marginLeft: 16 }}
                    placeholder="選択してください"
                  >
                    <Option value="locations">エリア</Option>
                    <Option value="equipments">設備</Option>
                    <Option value="parts">部品・消耗品</Option>
                    <Option value="suppliers">業者</Option>
                  </Select>
                </div>
              </>
            )}
          </Card>

          <Card>
            <Space>
              <Button onClick={resetImport}>
                戻る
              </Button>
              <Button
                type="primary"
                onClick={handleConfirmType}
                loading={previewMutation.isPending}
                disabled={!importType}
              >
                次へ
              </Button>
            </Space>
          </Card>
        </Space>
      )}

      {/* Step 2: プレビュー確認 */}
      {currentStep === 2 && previewData && (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Card title="インポートサマリー">
            <Space size="large" wrap>
              <Tag color="blue">
                <FileTextOutlined /> 合計: {previewData.totalRows}件
              </Tag>
              <Tag color="green">
                インポートタイプ: {importType ? getImportTypeLabel(importType) : '-'}
              </Tag>
            </Space>
          </Card>

          <Card title="重複データの処理方法">
            <Alert
              message="同一IDのデータが既に存在する場合の処理を選択してください"
              description="更新用にエクスポートしたCSVを再取り込みする場合は、通常「上書き」を選択してください。"
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Radio.Group
              value={duplicateHandling}
              onChange={(e) => setDuplicateHandling(e.target.value)}
            >
              <Space direction="vertical">
                <Radio value="upsert">
                  <Space>
                    <Text strong>上書き</Text>
                    <Text type="secondary">- 既存データを更新し、新規データを追加</Text>
                  </Space>
                </Radio>
                <Radio value="skip">
                  <Space>
                    <Text strong>スキップ（新規追加のみ）</Text>
                    <Text type="secondary">- 既存データを変更せず、新規データのみ追加</Text>
                  </Space>
                </Radio>
                <Radio value="error">
                  <Space>
                    <Text strong>エラー</Text>
                    <Text type="secondary">- 重複があれば処理を中止</Text>
                  </Space>
                </Radio>
              </Space>
            </Radio.Group>
            {duplicateHandling === 'upsert' && (
              <Alert
                message="注意: 上書きモードでは既存データが変更されます"
                type="warning"
                showIcon
                style={{ marginTop: 16 }}
              />
            )}
          </Card>

          {previewData.rows.length > 0 && (
            <Card title="データプレビュー（先頭5件）">
              <ResponsiveTable<PreviewRecord>
                columns={getPreviewColumns()}
                dataSource={previewData.rows.slice(0, 5)}
                pagination={false}
                scrollX={getTableScrollWidth()}
                size="small"
                rowKey={(record) => String(record._row ?? JSON.stringify(record))}
                mobileCardConfig={{
                  titleField: previewData.columns[0],
                }}
              />
              {previewData.totalRows > 5 && (
                <Text type="secondary" style={{ marginTop: 8, display: 'block' }}>
                  他 {previewData.totalRows - 5} 件...
                </Text>
              )}
            </Card>
          )}

          <Card>
            <Space>
              <Button onClick={() => setCurrentStep(1)}>
                戻る
              </Button>
              <Button
                type="primary"
                onClick={handleImport}
                loading={importing}
                disabled={previewData.rows.length === 0}
              >
                インポート実行
              </Button>
            </Space>
          </Card>

          {importing && (
            <Card>
              <Progress percent={importProgress} status="active" />
              <Text>データをインポート中...</Text>
            </Card>
          )}
        </Space>
      )}

      {/* Step 3: インポート完了 */}
      {currentStep === 3 && importResult && (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Card>
            <Result
              status={importResult.skipped > 0 ? 'warning' : 'success'}
              title="インポート完了"
              subTitle={`${importResult.imported}件のデータが正常に処理されました`}
              extra={[
                <Button type="primary" key="dashboard" onClick={() => navigate('/app/dashboard', { replace: true })}>
                  ダッシュボードへ
                </Button>,
                <Button key="new" onClick={resetImport}>
                  新規インポート
                </Button>,
              ]}
            />
          </Card>

          {importResult.skipped > 0 && (
            <Card title={`スキップ/エラー（${importResult.skipped}件）`} style={{ borderColor: '#faad14' }}>
              <Alert
                message="以下のデータはエラーまたはスキップされました"
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
              />
              <ResponsiveTable<ImportError>
                columns={[
                  {
                    title: '行番号',
                    dataIndex: 'row',
                    key: 'row',
                    width: 100,
                    mobileRole: 'tag',
                    mobilePriority: 1,
                  },
                  {
                    title: 'フィールド',
                    dataIndex: 'field',
                    key: 'field',
                    width: 150,
                    render: (field: string | undefined) => field || '-',
                    mobileRole: 'tag',
                    mobilePriority: 2,
                  },
                  {
                    title: 'エラー内容',
                    dataIndex: 'message',
                    key: 'message',
                    mobileRole: 'description',
                    mobilePriority: 3,
                  },
                ]}
                dataSource={importResult.errors}
                pagination={{ pageSize: 10 }}
                size="small"
                rowKey={(record) => `${record.row}-${record.field || 'unknown'}`}
                mobileCardConfig={{
                  titleField: 'message',
                }}
              />
            </Card>
          )}
        </Space>
      )}
    </PageContainer>
  );
}
